import axios from 'axios';
import { API_BASE_URL } from '@utils/constants';

/**
 * Axios instance with default configuration
 */
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

/**
 * Request interceptor
 * Adds authentication token if available
 */
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor
 * Handles common response scenarios
 */
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    // Handle specific error cases
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      
      switch (status) {
        case 401:
          // Unauthorized - clear token and redirect to login
          localStorage.removeItem('auth_token');
          if (window.location.pathname !== '/login') {
            // window.location.href = '/login';
          }
          break;
          
        case 403:
          // Forbidden
          console.error('Access forbidden:', data.message);
          break;
          
        case 404:
          // Not found
          console.error('Resource not found:', data.message);
          break;
          
        case 422:
          // Validation error
          console.error('Validation error:', data.errors);
          break;
          
        case 500:
          // Server error
          console.error('Server error:', data.message);
          break;
          
        default:
          console.error('API error:', data.message);
      }
      
      return Promise.reject(error.response.data);
    } else if (error.request) {
      // Request made but no response received
      console.error('No response from server:', error.request);
      return Promise.reject({
        message: 'Unable to connect to server. Please check your internet connection.',
      });
    } else {
      // Something else happened
      console.error('Request error:', error.message);
      return Promise.reject({
        message: error.message || 'An unexpected error occurred.',
      });
    }
  }
);

/**
 * Generic GET request
 * @param {string} url - Endpoint URL
 * @param {object} params - Query parameters
 * @returns {Promise} Response data
 */
export const get = (url, params = {}) => {
  return api.get(url, { params });
};

/**
 * Generic POST request
 * @param {string} url - Endpoint URL
 * @param {object} data - Request body
 * @returns {Promise} Response data
 */
export const post = (url, data = {}) => {
  return api.post(url, data);
};

/**
 * Generic PUT request
 * @param {string} url - Endpoint URL
 * @param {object} data - Request body
 * @returns {Promise} Response data
 */
export const put = (url, data = {}) => {
  return api.put(url, data);
};

/**
 * Generic PATCH request
 * @param {string} url - Endpoint URL
 * @param {object} data - Request body
 * @returns {Promise} Response data
 */
export const patch = (url, data = {}) => {
  return api.patch(url, data);
};

/**
 * Generic DELETE request
 * @param {string} url - Endpoint URL
 * @returns {Promise} Response data
 */
export const del = (url) => {
  return api.delete(url);
};

/**
 * Upload file(s)
 * @param {string} url - Endpoint URL
 * @param {FormData} formData - Form data with file(s)
 * @param {function} onProgress - Progress callback
 * @returns {Promise} Response data
 */
export const upload = (url, formData, onProgress = null) => {
  return api.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentCompleted);
      }
    },
  });
};

/**
 * Download file
 * @param {string} url - Endpoint URL
 * @param {string} filename - File name
 * @returns {Promise} Response data
 */
export const download = async (url, filename) => {
  try {
    const response = await api.get(url, {
      responseType: 'blob',
    });
    
    // Create blob link to download
    const blobUrl = window.URL.createObjectURL(new Blob([response]));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', filename);
    
    // Append to html link element page
    document.body.appendChild(link);
    
    // Start download
    link.click();
    
    // Clean up and remove the link
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
    
    return { success: true };
  } catch (error) {
    console.error('Download error:', error);
    return { success: false, error };
  }
};

export default api;